// Манифест пака размножения — КОНТРАКТ ПРИЁМКИ материала от подрядчика.
//
// Зачем схема, а не «договорились на словах»: первые четыре пака приехали в
// трёх разных форматах (голый массив с полем `shop`; голый массив без имени
// мерчанта вовсе; объект с `rows` и китайскими названиями), и каждый пришлось
// разбирать руками — сопоставлять имена брендов с латиницей, восстанавливать
// связь «файл ↔ строка» по порядку сортировки и переделывать категорию уже
// после отгрузки. Одна схема снимает всё это на входе.
//
// Область: КУПОНЫ и ГИФТ-КАРТЫ (по билетам работа закрыта 2026-08-07).
// Регламент для подрядчика — docs/flows/dev-manifest-spec.md, пример —
// docs/flows/examples/dev-manifest.example.json.
import { z } from 'zod';
import { merchantNameOk } from '../pothos/merchant';

/** Категория ассета. Живёт НА СТРОКЕ: пак «купоны 900» по факту разделился на
 *  366 купонов и 514 гифт-карт, и переделывать пришлось всё — от карточки до
 *  индекса (zh_TW, 2026-08). */
export const ManifestCategorySchema = z.enum(['coupon', 'gift_card']);
export type ManifestCategory = z.infer<typeof ManifestCategorySchema>;

/** Символики, которые умеет наш декодер и понимает отгрузочная таблица. */
export const ManifestSymbologySchema = z.enum([
  'Code128', 'QR', 'EAN13', 'EAN8', 'UPCA', 'UPCE',
  'Code39', 'Code93', 'ITF', 'DataMatrix', 'PDF417', 'Aztec', 'Codabar',
]);

const MERCHANT_MAX = 40;

export const ManifestRowSchema = z.object({
  /** Имя файла в папке исходников — ТОЧНОЕ, с расширением. Единственная связь
   *  строки с картинкой: восстанавливать её по порядку сортировки нельзя. */
  file: z.string().trim().min(1).max(200),
  /** Категория этого кадра. */
  category: ManifestCategorySchema,
  /** Бренд ЛАТИНИЦЕЙ — едет в таблицу заказчика и в имя файла, склеенное
   *  подчёркиваниями, поэтому `&`, `/`, апострофы и иероглифы недопустимы. */
  merchant: z.string().trim().min(1).max(MERCHANT_MAX).refine(merchantNameOk, {
    message: 'merchant: только латиница, цифры и пробел',
  }),
  /** Оригинальное написание с экрана — для сверки, в отгрузку не идёт. */
  merchant_local: z.string().trim().max(120).default(''),
  /** УНИКАЛЬНЫЙ код кадра: по нему дедуп и матч. Товарный штрихкод (один на
   *  сотню купонов) сюда класть нельзя — для него есть product_code. */
  unique_code: z.string().trim().min(1).max(120),
  code_symbology: ManifestSymbologySchema,
  /** Товарный код, если он на экране есть. Идентификатором НЕ считается. */
  product_code: z.string().trim().max(120).default(''),
  /** Срок действия, ISO `YYYY-MM-DD`. */
  valid_until: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, 'valid_until: формат YYYY-MM-DD').or(z.literal('')).default(''),
  /** Что за оффер — заголовок как на экране. */
  title: z.string().trim().max(300).default(''),
  /** Свободные поля пака (цены, ссылки, служебное) — не мешают приёмке. */
}).passthrough();
export type ManifestRow = z.infer<typeof ManifestRowSchema>;

export const ManifestSchema = z.object({
  /** Площадка/приложение, откуда материал: `tlc`, `lazada`, `ilc`. */
  provider: z.string().trim().min(1).max(60),
  /** Код локали ровно как в плане: `zh_TW`, `ms_MY`, `id_ID`. */
  locale: z.string().trim().regex(/^[a-z]{2}_[A-Z]{2}$/, 'locale: формат xx_XX'),
  /** Страна в написании отгрузочной таблицы: `Taiwan`, `Malaysia`. */
  country: z.string().trim().min(2).max(60),
  /** Носитель: скриншот приложения / сайта / фото. */
  medium: z.enum(['app_screen', 'web_screen', 'photo']).default('app_screen'),
  /** Дата сборки пака, ISO. */
  as_of: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, 'as_of: формат YYYY-MM-DD'),
  /** Версия пака: правки после проверки увеличивают номер. */
  revision: z.number().int().positive().default(1),
  /** Сколько строк ДОЛЖНО быть — сверяется с длиной rows. */
  count: z.number().int().positive(),
  rows: z.array(ManifestRowSchema).min(1),
}).passthrough();
export type Manifest = z.infer<typeof ManifestSchema>;

export interface ManifestProblem { row: number; field: string; message: string }

/**
 * Полная проверка пака. Схема ловит форму, а это — то, что схемой не выразить:
 * заявленный count, уникальность кода и совпадение с папкой исходников.
 * `files` — имена файлов в папке; не передан → проверка файлов пропускается.
 */
export function checkManifest(input: unknown, files?: string[]): ManifestProblem[] {
  const parsed = ManifestSchema.safeParse(input);
  if (!parsed.success) {
    return parsed.error.issues.map((i) => ({
      row: typeof i.path[1] === 'number' ? i.path[1] + 1 : 0,
      field: i.path.filter((p) => typeof p === 'string').join('.') || '(корень)',
      message: i.message,
    }));
  }
  const m = parsed.data;
  const out: ManifestProblem[] = [];

  if (m.count !== m.rows.length) {
    out.push({ row: 0, field: 'count', message: `заявлено ${m.count}, строк ${m.rows.length}` });
  }

  const seenCode = new Map<string, number>();
  const seenFile = new Map<string, number>();
  m.rows.forEach((r, i) => {
    const n = i + 1;
    const prevCode = seenCode.get(r.unique_code);
    if (prevCode) out.push({ row: n, field: 'unique_code', message: `повтор кода из строки ${prevCode}` });
    else seenCode.set(r.unique_code, n);

    const prevFile = seenFile.get(r.file);
    if (prevFile) out.push({ row: n, field: 'file', message: `файл уже указан в строке ${prevFile}` });
    else seenFile.set(r.file, n);

    if (r.product_code && r.product_code === r.unique_code) {
      out.push({ row: n, field: 'unique_code', message: 'совпадает с product_code — это товарный штрихкод, а не идентификатор' });
    }
  });

  if (files) {
    const have = new Set(files);
    m.rows.forEach((r, i) => {
      if (!have.has(r.file)) out.push({ row: i + 1, field: 'file', message: `нет файла «${r.file}» в папке исходников` });
    });
    const used = new Set(m.rows.map((r) => r.file));
    for (const f of files) {
      if (!used.has(f)) out.push({ row: 0, field: 'file', message: `файл «${f}» есть в папке, но его нет в манифесте` });
    }
  }
  return out;
}
